import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'Void Pre-Auth Process' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-csharp', label: 'Sample Void Pre-Auth Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'void-reasons', label: 'Reason Codes Reference' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample Void Pre-Auth XML' },
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'expected-responses', label: 'Expected XML Documents' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=reversalauth&Data=<R>...</R>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
    UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

    string pData = "username=SomeName"; //API Username
    pData += "&password=SomePassword"; //API Password
    pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
    pData += "&ApiSignature=Reversalauth"; //API Signature - Please notice the \`Reversalauth\` signature here for the Void Pre-Auth request

    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>"); //Data fields required for request

    byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

    httpWReq.Method = "POST"; //HTTP Method - POST
    httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
    httpWReq.ContentLength = data.Length; //Getting Content Length

    byte[] byteArray = Encoding.UTF8.GetBytes(pData);
    Stream dataStream = httpWReq.GetRequestStream();
    dataStream.Write(byteArray, 0, byteArray.Length);
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse();
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd();

    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);

    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    return View();
}`;

const VOID_XML = `<!-- Void Pre-Auth Request Demonstration -->
<R>
    <R1>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R1>
    <R2>1000596</R2>
    <R3>1</R3>
</R>`;

const RESP_XML = `<!-- Sample Server Response, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0050</R1>
    <R2>Void pre-authorisation received</R2>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: 'Transaction Code — the Transaction Code used in the Sale request.', req: 'Y', def: 'AN(40)' },
  { name: 'R2', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R3', desc: 'Reason for Void Pre-Auth — a single digit reason code (see Reason Codes Reference below).', req: 'Y', def: 'AN(50)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'Transaction Code.', regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2', desc: 'Vendor ID.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R3', desc: 'Reason For Void Pre-Auth.', regex: '^[0-9]{1}$' },
];

const RESP_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
];

const VoidPreAuth = () => (
  <ContainerPageLayout
    currentId="void-pre-auth"
    title="Void Pre-Auth"
    subtitle="Cancels a previously authorised Pre-Auth and releases the frozen funds back into the cardholder's account."
    signature="reversalauth"
    toc={TOC}
    mainTestId="void-preauth-page"
    sidebarTestId="void-preauth-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed">
        The <strong>Void Pre-Auth</strong> API is only required if the merchant wants to Void a Pre-Auth. Voiding a Pre-Auth cancels the frozen funds from a <em>"Pending"</em> state and releases the funds back into the consumer's bank account.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>When to void vs refund:</strong> Use <strong>Void Pre-Auth</strong> while the funds are still frozen (before{' '}
          <Link to="/technical-documentation/api-reference/complete-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Complete Pre-Auth</Link>{' '}
          has been called). If the transaction has already been cleared/captured, you'll need to use <span className="text-gray-400 italic">Refund</span> instead.
        </p>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Void Pre-Auth Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The process is simple and straightforward. Below demonstrates the steps required to successfully make the request.
      </p>
      <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-base leading-relaxed">
        <li>The transaction information is submitted to our API.</li>
        <li>
          The merchant will receive a response of <strong className="text-[#c32222]">"0050 — Void Pre-Authorisation Received"</strong> which indicates a confirmation notification will be sent to the merchant's Notification URL with the overall result. The confirmation notification may contain the response <strong className="text-[#c32222]">"0000 — Successful"</strong> indicating that the transaction in question is now voided.
        </li>
      </ol>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Void Pre-Auth</code> call must be composed of the following information:
      </p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = Reversalauth</li>
        <li><strong>Data</strong> = Form data in XML format</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-csharp" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample Void Pre-Auth Request</h3>
          <DownloadButtons />
        </div>
        <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-csharp-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="Reversalauth"
          title="Request Reference — Void Pre-Auth"
          description="Cancel a previously authorised Pre-Auth."
          defaultData={VOID_XML.replace('<!-- Void Pre-Auth Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Void Pre-Auth</code> request.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Reason Codes — value-add: visual reference for R3 */}
    <section id="void-reasons" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Reason Codes Reference</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        Supply a single digit into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R3&gt;</code> field. The accepted codes depend on the card scheme of the original Sale transaction:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-lg border-2 border-[#1A1F71]/20 bg-gradient-to-br from-white to-[#1A1F71]/5 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="px-3 py-1 bg-[#1A1F71] text-white text-xs font-bold rounded uppercase tracking-wide">Visa</div>
            <span className="text-sm text-gray-600">3 reason codes</span>
          </div>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">1</code>
              <span>Transaction Voided By Customer</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">2</code>
              <span>Transaction Not Completed</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#1A1F71] text-white rounded font-mono font-bold text-center leading-6">3</code>
              <span>No Confirmation From Point Of Service</span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border-2 border-[#EB001B]/20 bg-gradient-to-br from-white to-[#FF5F00]/5 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="px-3 py-1 bg-gradient-to-r from-[#EB001B] to-[#FF5F00] text-white text-xs font-bold rounded uppercase tracking-wide">Mastercard</div>
            <span className="text-sm text-gray-600">4 reason codes</span>
          </div>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">1</code>
              <span>Customer Cancellation</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">2</code>
              <span>Error</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">3</code>
              <span>Response Received Late</span>
            </li>
            <li className="flex gap-3">
              <code className="flex-shrink-0 w-6 h-6 bg-[#EB001B] text-white rounded font-mono font-bold text-center leading-6">4</code>
              <span>Suspect Fraud</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Mismatch warning:</strong> If the reason code doesn't match the card scheme of the original transaction, you may receive error{' '}
          <code className="bg-white px-1 py-0.5 rounded font-mono text-[#c32222] font-semibold">0066 — Void Reason chosen does not match the Card Type voiding reason</code>.
          Always derive the scheme from the original Sale response before picking a reason code.
        </p>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Void Pre-Auth</code> request including its validation.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation (Regex)</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">Void Pre-Auth</code> XML Document</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <CodeBlock code={VOID_XML} lang="xml" testid="void-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">Below are the expected XML fields returned from your request.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {RESP_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Expected Responses */}
    <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validation.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
        <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong className="text-[#c32222]">0050</strong> — Indicates the confirmation notification will be sent to the Notification URL.</li>
        </ol>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0050)</h3>
      <CodeBlock code={RESP_XML} lang="xml" testid="resp-xml-code" />
    </section>
  </ContainerPageLayout>
);

export default VoidPreAuth;
